import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, Store, MapPin, CalendarDays } from "lucide-react";
import { useAuth, useCabang } from "@/context/AuthContext";
import { periodLabel } from "@/lib/format";
import { generateReportPDF } from "@/lib/reportPdf";

const MONTHS_ID = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];

// Hitung rentang tanggal berdasarkan tipe periode + input user
function computeRange(periodType, dateStr) {
  if (periodType === "daily") return [dateStr, dateStr];
  if (periodType === "monthly") {
    const [y, m] = dateStr.split("-").map(Number);
    const first = `${y}-${String(m).padStart(2, "0")}-01`;
    const last = new Date(y, m, 0).getDate();
    return [first, `${y}-${String(m).padStart(2, "0")}-${String(last).padStart(2, "0")}`];
  }
  if (periodType === "yearly") {
    const y = Number(dateStr);
    return [`${y}-01-01`, `${y}-12-31`];
  }
  if (periodType === "weekly") {
    const d = new Date(dateStr + "T00:00:00");
    const day = d.getDay() || 7; // 1..7 (Sen..Min)
    const mon = new Date(d); mon.setDate(d.getDate() - (day - 1));
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    const iso = (dt) => dt.toISOString().slice(0, 10);
    return [iso(mon), iso(sun)];
  }
  return [dateStr, dateStr];
}

function humanRange(periodType, dateStr) {
  const [f, t] = computeRange(periodType, dateStr);
  if (periodType === "daily") return f;
  if (periodType === "monthly") {
    const [y, m] = dateStr.split("-").map(Number);
    return `${MONTHS_ID[m - 1]} ${y}`;
  }
  if (periodType === "yearly") return `Tahun ${dateStr}`;
  return `${f} s/d ${t}`;
}

export default function Laporan() {
  const { user } = useAuth();
  const { cabangList } = useCabang();
  const isSuper = user?.role === "super_admin";
  const isCabangUser = user?.role === "cabang";
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role === "viewer") navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const [admins, setAdmins] = useState([]);
  const [adminId, setAdminId] = useState("");
  const [cabangId, setCabangId] = useState(isCabangUser ? user?.cabang_id : "all");
  const [periodType, setPeriodType] = useState("monthly");
  const today = new Date();
  const [dateStr, setDateStr] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!isSuper) return;
    api.get("/users")
      .then(({ data }) => setAdmins(data.filter((u) => u.role === "admin" && (u.workspace_id === user?.active_workspace_id || u.active_workspace_id === user?.active_workspace_id))))
      .catch(() => setAdmins([]));
  }, [isSuper, user?.active_workspace_id]);

  const visibleCabang = useMemo(() => {
    if (!isSuper) return cabangList;
    if (!adminId) return [];
    return cabangList.filter((c) => c.admin_id === adminId);
  }, [cabangList, isSuper, adminId]);

  // Sinkronkan format input date sesuai periodType
  useEffect(() => {
    const t = new Date();
    if (periodType === "daily" || periodType === "weekly") {
      setDateStr(t.toISOString().slice(0, 10));
    } else if (periodType === "monthly") {
      setDateStr(`${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`);
    } else if (periodType === "yearly") {
      setDateStr(String(t.getFullYear()));
    }
  }, [periodType]);

  const canGenerate = (!isSuper || adminId) && cabangId && dateStr;

  const generatePDF = async () => {
    if (!canGenerate) { toast.error("Lengkapi semua pilihan terlebih dahulu"); return; }
    setDownloading(true);
    try {
      const [from, to] = computeRange(periodType, dateStr);
      const params = { period: periodType, tanggal_from: from, tanggal_to: to };
      if (cabangId && cabangId !== "all") params.cabang_id = cabangId;
      if (isSuper && adminId) params.admin_id = adminId;
      const detailParams = { tanggal_from: from, tanggal_to: to };
      if (params.cabang_id) detailParams.cabang_id = params.cabang_id;
      if (params.admin_id) detailParams.admin_id = params.admin_id;
      const [{ data }, { data: detail }] = await Promise.all([
        api.get("/dashboard/summary", { params }),
        api.get("/dashboard/detail", { params: detailParams }),
      ]);

      const usahaLabel = isSuper ? (admins.find((a) => a.id === adminId)?.name || "—") : (user?.name || "—");
      const cabangLabel = cabangId === "all" ? "Semua Cabang" : (cabangList.find((c) => c.id === cabangId)?.name || "—");
      generateReportPDF({
        usahaLabel, cabangLabel, periodType,
        periodRangeLabel: humanRange(periodType, dateStr),
        data, detail,
        filename: `Laporan_${usahaLabel.replace(/\s+/g, "_")}_${cabangLabel.replace(/\s+/g, "_")}_${dateStr}.pdf`,
      });
      toast.success("PDF berhasil diunduh");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuat laporan");
    } finally { setDownloading(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={<span className="inline-flex items-center gap-2"><FileDown className="h-6 w-6 text-emerald-600" /> Laporan PDF</span>}
        subtitle="Pilih usaha, cabang, dan periode — lalu unduh laporan A4 vertikal siap cetak."
      />

      <Card className="p-6 border-slate-200">
        <div className={`grid grid-cols-1 ${isSuper ? "md:grid-cols-4" : "md:grid-cols-3"} gap-4 items-end`}>
          {isSuper && (
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700 inline-flex items-center gap-1.5"><Store className="h-3.5 w-3.5" /> Usaha</Label>
              <Select value={adminId} onValueChange={setAdminId}>
                <SelectTrigger data-testid="report-usaha" className="h-11"><SelectValue placeholder="Pilih usaha" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {admins.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-slate-500">Belum ada usaha</div>
                  ) : admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700 inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Cabang</Label>
            <Select value={cabangId} onValueChange={setCabangId} disabled={isCabangUser || (isSuper && !adminId)}>
              <SelectTrigger data-testid="report-cabang" className="h-11">
                <SelectValue placeholder={isSuper && !adminId ? "Pilih usaha dulu" : "Pilih cabang"} />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Semua Cabang</SelectItem>
                {visibleCabang.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700 inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Jenis Periode</Label>
            <Select value={periodType} onValueChange={setPeriodType}>
              <SelectTrigger data-testid="report-period-type" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="daily">Harian</SelectItem>
                <SelectItem value="weekly">Mingguan</SelectItem>
                <SelectItem value="monthly">Bulanan</SelectItem>
                <SelectItem value="yearly">Tahunan</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">
              {periodType === "daily" && "Pilih Tanggal"}
              {periodType === "weekly" && "Pilih Minggu (tanggal dalam minggu)"}
              {periodType === "monthly" && "Pilih Bulan"}
              {periodType === "yearly" && "Pilih Tahun"}
            </Label>
            {periodType === "yearly" ? (
              <Input type="number" min="2000" max="2100" value={dateStr} onChange={(e) => setDateStr(e.target.value)}
                     data-testid="report-date" className="h-11" />
            ) : periodType === "monthly" ? (
              <Input type="month" value={dateStr} onChange={(e) => setDateStr(e.target.value)}
                     data-testid="report-date" className="h-11" />
            ) : (
              <Input type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)}
                     data-testid="report-date" className="h-11" />
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="text-sm text-slate-600">
            {canGenerate ? (
              <span>Rentang: <b>{humanRange(periodType, dateStr)}</b></span>
            ) : (
              <span className="text-slate-400">Lengkapi pilihan di atas</span>
            )}
          </div>
          <Button size="lg" onClick={generatePDF} disabled={!canGenerate || downloading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="download-pdf-btn">
            <FileDown className="h-4 w-4 mr-2" /> {downloading ? "Menyiapkan..." : "Unduh PDF A4"}
          </Button>
        </div>
      </Card>

      <Card className="p-6 border-slate-200 bg-slate-50/50">
        <div className="font-heading text-sm font-bold text-slate-900 mb-2">Isi Laporan PDF</div>
        <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
          <li>Header: Usaha, Cabang, Periode, dan waktu pembuatan</li>
          <li>Ringkasan: Total Pendapatan, Pengeluaran, dan Laba/Rugi</li>
          <li>Rincian Anggaran vs Realisasi per Kategori (Bahan Baku, Operasional, Insentif, Lainnya)</li>
          <li>Rincian per periode (harian/mingguan/bulanan/tahunan)</li>
        </ul>
      </Card>
    </div>
  );
}
